// frontend/src/pages/Checkins.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  getCheckins, createCheckin, updateCheckin, deleteCheckin, getUsers,
  getReviewSessions, createReviewSession, updateReviewSession, deleteReviewSession,
  getCycles
} from '../services/api';
import {
  Card, Avatar, Badge, Btn, Modal, Field, Input, Textarea, Select,
  SectionTitle, Spinner
} from '../components/UI';
import PageHeader from '../components/PageHeader';

const STATUS_COLOR = { scheduled: 'blue', complete: 'green', overdue: 'red', cancelled: 'gray' };

// Null-safe date/time formatting — avoids rendering "Invalid Date" for a missing timestamp.
const fmtDate = s => s ? new Date(s).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtTime = s => s ? new Date(s).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : '';

export default function Checkins() {
  const { user } = useAuth();
  const [checkins,  setCheckins]  = useState([]);
  const [sessions,  setSessions]  = useState([]);
  const [users,     setUsers]     = useState([]);
  const [cycles,    setCycles]    = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState('sessions');

  const [selected,   setSelected]   = useState(null);
  const [showNew,    setShowNew]    = useState(false);
  const [showNewSes, setShowNewSes] = useState(false);
  const [editSes,    setEditSes]    = useState(null);
  const [saving,     setSaving]     = useState(false);

  const [newForm,    setNewForm]    = useState({ employee_id: '', scheduled_at: '' });
  const [sesForm,    setSesForm]    = useState({ reviewee_id: '', reviewer_id: user?.id || '', cycle_id: '', scheduled_at: '', location: '', agenda: '' });
  const [notesForm,  setNotesForm]  = useState({ status: '', manager_notes: '', employee_notes: '', action_items: '', scheduled_at: '' });

  const [filterManager, setFilterManager] = useState('');

  const isEmployee = user?.role === 'employee';
  const isAdmin    = user?.role === 'admin';
  const canManage  = ['admin','manager','dept_head'].includes(user?.role);

  const load = async () => {
    const [c, s] = await Promise.all([getCheckins().catch(() => []), getReviewSessions().catch(() => [])]);
    setCheckins(Array.isArray(c) ? c : []); setSessions(Array.isArray(s) ? s : []);
  };

  useEffect(() => {
    Promise.all([
      getCheckins().catch(() => []),
      getReviewSessions().catch(() => []),
      getUsers().catch(() => []),
      getCycles().catch(() => []),
    ])
      .then(([c, s, u, cy]) => {
        setCheckins(Array.isArray(c) ? c : []);
        setSessions(Array.isArray(s) ? s : []);
        setUsers((Array.isArray(u) ? u : []).filter(x => x.id !== user?.id));
        setCycles(Array.isArray(cy) ? cy : []);
      })
      .finally(() => setLoading(false));
  }, []);

  const openNotes = c => {
    setSelected(c);
    setNotesForm({
      status: c.status, manager_notes: c.manager_notes || '',
      employee_notes: c.employee_notes || '', action_items: c.action_items || '',
      scheduled_at: c.scheduled_at?.slice(0, 16) || '',
    });
  };

  const handleCreateCheckin = async e => {
    e.preventDefault(); setSaving(true);
    try {
      // Employees just send scheduled_at — backend uses their manager
      const payload = isEmployee
        ? { scheduled_at: newForm.scheduled_at }
        : newForm;
      await createCheckin(payload);
      await load(); setShowNew(false);
      setNewForm({ employee_id: '', scheduled_at: '' });
    } catch (err) { alert(err.response?.data?.error || 'Error scheduling check-in'); }
    finally { setSaving(false); }
  };

  const handleSaveNotes = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await updateCheckin(selected.id, notesForm);
      await load(); setSelected(null);
    } catch (err) { alert('Error saving notes'); }
    finally { setSaving(false); }
  };

  const handleDeleteCheckin = async id => {
    if (!window.confirm('Archive this check-in? It will be hidden from the system.')) return;
    await deleteCheckin(id); await load();
  };

  const handleCreateSession = async e => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = isEmployee
        ? { ...sesForm, reviewee_id: user.id, reviewer_id: user.manager_id }
        : sesForm;
      await createReviewSession(payload);
      await load(); setShowNewSes(false);
      setSesForm({ reviewee_id: '', reviewer_id: user?.id || '', cycle_id: '', scheduled_at: '', location: '', agenda: '' });
    } catch (err) { alert(err.response?.data?.error || 'Error scheduling session'); }
    finally { setSaving(false); }
  };

  const handleUpdateSession = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await updateReviewSession(editSes.id, sesForm);
      await load(); setEditSes(null);
    } catch (err) { alert('Error updating session'); }
    finally { setSaving(false); }
  };

  const handleDeleteSession = async id => {
    if (!window.confirm('Archive this review session? It will be hidden from the system.')) return;
    await deleteReviewSession(id); await load();
  };

  const openEditSession = s => {
    setEditSes(s);
    setSesForm({
      reviewee_id: String(s.reviewee_id), reviewer_id: String(s.reviewer_id),
      cycle_id: String(s.cycle_id || ''), scheduled_at: s.scheduled_at?.slice(0, 16) || '',
      location: s.location || '', agenda: s.agenda || '', status: s.status,
    });
  };

  const filteredCheckins = filterManager
    ? checkins.filter(c => String(c.manager_id) === filterManager)
    : checkins;

  const grouped = {
    overdue:   filteredCheckins.filter(c => c.status === 'overdue'),
    scheduled: filteredCheckins.filter(c => c.status === 'scheduled'),
    complete:  filteredCheckins.filter(c => c.status === 'complete'),
  };

  const filteredSessions = filterManager
    ? sessions.filter(s => String(s.reviewer_id) === filterManager)
    : sessions;

  const sesByStatus = {
    scheduled: filteredSessions.filter(s => s.status === 'scheduled'),
    complete:  filteredSessions.filter(s => s.status === 'complete'),
    cancelled: filteredSessions.filter(s => s.status === 'cancelled'),
  };

  const managers = [...users.filter(u => ['admin','manager'].includes(u.role)),
    ...(user?.role === 'admin' || user?.role === 'manager' ? [{ id: user.id || user.user_id, name: user.name }] : [])]
    .filter((m, i, arr) => arr.findIndex(x => x.id === m.id) === i);

  if (loading) return <Spinner />;

  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <PageHeader moduleKey="checkins" helpTask="run-checkins" subtitle={isEmployee ? 'Your 1:1s and review sessions' : 'Scheduled sessions with your team'} />
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isAdmin && (
            <select value={filterManager} onChange={e => setFilterManager(e.target.value)}
              style={{ padding: '6px 10px', fontSize: 12, border: '0.5px solid var(--border-md)', borderRadius: 'var(--radius-md)', background: 'var(--surface)', color: 'var(--text-1)' }}>
              <option value="">All managers</option>
              {managers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          )}
          <Btn variant="secondary" size="sm" onClick={() => { setSesForm(f => ({ ...f, reviewer_id: user?.id || '' })); setShowNewSes(true); }}>+ Review session</Btn>
          <Btn size="sm" onClick={() => setShowNew(true)}>+ 1:1 check-in</Btn>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 2, borderBottom: '0.5px solid var(--border)', marginBottom: 24 }}>
        {[['sessions','Review sessions'], ['checkins','1:1 Check-ins']].map(([id, label]) => (
          <button key={id} onClick={() => setActiveTab(id)}
            style={{
              padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: activeTab === id ? 500 : 400,
              color: activeTab === id ? 'var(--navy-600)' : 'var(--text-3)',
              borderBottom: `2px solid ${activeTab === id ? 'var(--navy-600)' : 'transparent'}`,
              marginBottom: -1,
            }}>
            {label}
            {id === 'checkins' && grouped.overdue.length > 0 && (
              <span style={{ marginLeft: 6, background: 'var(--red-500)', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 10 }}>
                {grouped.overdue.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── 1:1 CHECK-INS ── */}
      {activeTab === 'checkins' && (
        <div>
          {grouped.overdue.length > 0 && (
            <div style={{ background: 'var(--red-50)', border: '0.5px solid #F09595', borderRadius: 8, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: 'var(--red-700)' }}>
              ⚠ <strong>{grouped.overdue.length} overdue</strong> check-in{grouped.overdue.length > 1 ? 's' : ''} — reschedule or mark complete.
            </div>
          )}
          {['overdue','scheduled','complete'].map(group => {
            const items = grouped[group];
            if (!items.length) return null;
            return (
              <div key={group} style={{ marginBottom: 24 }}>
                <SectionTitle>{group === 'overdue' ? 'Overdue' : group === 'scheduled' ? 'Upcoming' : 'Completed'}</SectionTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map(c => (
                    <Card key={c.id} style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Link to={`/profile/${c.employee_id}`} className="user-link" style={{ display: 'flex', flexShrink: 0, textDecoration: 'none' }}>
                          <Avatar initials={c.emp_initials} size={34} />
                        </Link>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Link to={`/profile/${c.employee_id}`} className="user-link" style={{ textDecoration: 'none' }}>
                            <div className="user-link-name" style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-1)' }}>{c.employee_name}</div>
                          </Link>
                          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                            with {c.manager_name} · {fmtDate(c.scheduled_at)}
                            {' '}at {fmtTime(c.scheduled_at)}
                          </div>
                          {c.action_items && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>Actions: {c.action_items}</div>}
                        </div>
                        <Badge color={STATUS_COLOR[c.status]}>{c.status}</Badge>
                        <Btn variant="secondary" size="sm" onClick={() => openNotes(c)}>
                          {c.status === 'scheduled' ? 'Start' : 'View notes'}
                        </Btn>
                        {canManage && (
                          <Btn variant="ghost" size="sm" onClick={() => handleDeleteCheckin(c.id)} style={{ color: 'var(--red-500)' }}>✕</Btn>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
          {checkins.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-3)' }}>
              No check-ins yet. {isEmployee ? 'Request one with your manager above.' : 'Schedule one above.'}
            </div>
          )}
        </div>
      )}

      {/* ── REVIEW SESSIONS ── */}
      {activeTab === 'sessions' && (
        <div>
          {['scheduled','complete','cancelled'].map(group => {
            const items = sesByStatus[group];
            if (!items.length) return null;
            return (
              <div key={group} style={{ marginBottom: 24 }}>
                <SectionTitle>{group}</SectionTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {items.map(s => (
                    <Card key={s.id} style={{ padding: '14px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{ display: 'flex', gap: -8 }}>
                          <Avatar initials={s.reviewer_initials} size={32} />
                          <Avatar initials={s.reviewee_initials} size={32} style={{ marginLeft: -10, border: '2px solid var(--surface)' }} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 500 }}>
                            {s.reviewer_name} → {s.reviewee_name}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>
                            {fmtDate(s.scheduled_at)}
                            {' '}at {fmtTime(s.scheduled_at)}
                            {s.cycle_name && ` · ${s.cycle_name}`}
                            {s.location && ` · ${s.location}`}
                          </div>
                          {s.agenda && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 3 }}>Agenda: {s.agenda}</div>}
                        </div>
                        <Badge color={STATUS_COLOR[s.status] || 'gray'}>{s.status}</Badge>
                        <Btn variant="secondary" size="sm" onClick={() => openEditSession(s)}>Edit</Btn>
                        <Btn variant="ghost" size="sm" onClick={() => handleDeleteSession(s.id)} style={{ color: 'var(--red-500)' }}>✕</Btn>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
          {sessions.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-3)' }}>
              No review sessions scheduled yet. Create one above.
            </div>
          )}
        </div>
      )}

      {/* ── New 1:1 modal ── */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Schedule 1:1 check-in" width={400}>
        <form onSubmit={handleCreateCheckin}>
          {isEmployee ? (
            <div style={{ background: 'var(--navy-50)', borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: 'var(--navy-600)' }}>
              This will request a 1:1 with your manager.
            </div>
          ) : (
            <Field label="Team member" required>
              <Select value={newForm.employee_id} onChange={e => setNewForm(f => ({ ...f, employee_id: e.target.value }))}>
                <option value="">Select team member…</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name} — {u.job_title}</option>)}
              </Select>
            </Field>
          )}
          <Field label="Date & time" required>
            <Input type="datetime-local" value={newForm.scheduled_at} onChange={e => setNewForm(f => ({ ...f, scheduled_at: e.target.value }))} />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={() => setShowNew(false)}>Cancel</Btn>
            <Btn disabled={saving || (!isEmployee && !newForm.employee_id) || !newForm.scheduled_at}>
              {saving ? 'Saving…' : 'Schedule'}
            </Btn>
          </div>
        </form>
      </Modal>

      {/* ── New review session modal ── */}
      <Modal open={showNewSes || !!editSes} onClose={() => { setShowNewSes(false); setEditSes(null); }} title={editSes ? 'Edit review session' : 'Schedule review session'}>
        <form onSubmit={editSes ? handleUpdateSession : handleCreateSession}>
          {!isEmployee && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Reviewer">
                <Select value={sesForm.reviewer_id} onChange={e => setSesForm(f => ({ ...f, reviewer_id: e.target.value }))}>
                  <option value={user?.id}>{user?.name} (me)</option>
                  {users.filter(u => ['admin','manager'].includes(u.role)).map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Reviewee" required>
                <Select value={sesForm.reviewee_id} onChange={e => setSesForm(f => ({ ...f, reviewee_id: e.target.value }))}>
                  <option value="">Select person…</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </Select>
              </Field>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Date & time" required>
              <Input type="datetime-local" value={sesForm.scheduled_at} onChange={e => setSesForm(f => ({ ...f, scheduled_at: e.target.value }))} />
            </Field>
            <Field label="Review cycle">
              <Select value={sesForm.cycle_id} onChange={e => setSesForm(f => ({ ...f, cycle_id: e.target.value }))}>
                <option value="">No specific cycle</option>
                {cycles.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
          </div>
          <Field label="Location / link">
            <Input value={sesForm.location} onChange={e => setSesForm(f => ({ ...f, location: e.target.value }))} placeholder="e.g. Zoom, Google Meet, Room 3A" />
          </Field>
          <Field label="Agenda">
            <Textarea value={sesForm.agenda} onChange={e => setSesForm(f => ({ ...f, agenda: e.target.value }))} placeholder="Topics to cover…" rows={3} />
          </Field>
          {editSes && (
            <Field label="Status">
              <Select value={sesForm.status} onChange={e => setSesForm(f => ({ ...f, status: e.target.value }))}>
                <option value="scheduled">Scheduled</option>
                <option value="complete">Complete</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            </Field>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Btn type="button" variant="secondary" onClick={() => { setShowNewSes(false); setEditSes(null); }}>Cancel</Btn>
            <Btn disabled={saving || !sesForm.scheduled_at || (!isEmployee && !sesForm.reviewee_id)}>
              {saving ? 'Saving…' : editSes ? 'Save changes' : 'Schedule session'}
            </Btn>
          </div>
        </form>
      </Modal>

      {/* ── Notes modal ── */}
      <Modal open={!!selected} onClose={() => setSelected(null)} title={`1:1 with ${selected?.employee_name}`}>
        {selected && (
          <form onSubmit={handleSaveNotes}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <Field label="Status">
                <Select value={notesForm.status} onChange={e => setNotesForm(f => ({ ...f, status: e.target.value }))}>
                  <option value="scheduled">Scheduled</option>
                  <option value="complete">Complete</option>
                  <option value="overdue">Overdue</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </Field>
              <Field label="Reschedule to">
                <Input type="datetime-local" value={notesForm.scheduled_at} onChange={e => setNotesForm(f => ({ ...f, scheduled_at: e.target.value }))} />
              </Field>
            </div>
            <Field label="Manager notes">
              <Textarea value={notesForm.manager_notes} onChange={e => setNotesForm(f => ({ ...f, manager_notes: e.target.value }))} placeholder="Discussion notes, observations…" rows={3} />
            </Field>
            <Field label="Employee notes">
              <Textarea value={notesForm.employee_notes} onChange={e => setNotesForm(f => ({ ...f, employee_notes: e.target.value }))} placeholder="Your updates and perspective…" rows={3} />
            </Field>
            <Field label="Action items">
              <Textarea value={notesForm.action_items} onChange={e => setNotesForm(f => ({ ...f, action_items: e.target.value }))} placeholder="Follow-up tasks…" rows={2} />
            </Field>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn type="button" variant="secondary" onClick={() => setSelected(null)}>Cancel</Btn>
              <Btn disabled={saving}>{saving ? 'Saving…' : 'Save notes'}</Btn>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
}
